import type { UseCurriculum_PersonalizedSegmentCourseFragment as DpPersonalizedSegmentCourse } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import type {
  UseCurriculum_DegreeCurriculumFragment as DpDegreeCurriculum,
  UseCurriculum_PbaCurriculumFragment as DpPbaCurriculum,
  UseCurriculum_GradeScaleFragment as GradeScaleFragment,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCurriculum';
import {
  curriculumHasGPAWarning,
  curriculumHasGradeWarning,
  getCreditsSummaryForSingleSegment,
  getWarningsForSingleCategory,
} from 'bundles/degree-home/components/v3-dpm/lib/gradeUtils';
import type { ProgressionCalculatorResult } from 'bundles/degree-home/components/v3-dpm/types/gradeTypes';
import { getSumOfCredits } from 'bundles/program-loggedin-shared/libraries/gradeUtils';

import _t from 'i18n!nls/degree-home';

/**
 * Calculate the text to be displayed for the credits completion
 * @param completedCredits the number of completed credits
 * @param inProgressCredits the number of in progress credits
 * @returns an array of completed credits, in progress and planned credits
 */
export const getCreditsCompletionText = (
  completedCredits: number | undefined,
  inProgressCredits: number | undefined
): string[] => {
  const creditsCompletionTextList: string[] = [];
  // the text is hidden if 0 credits are completed
  const completedCreditsText = completedCredits
    ? _t('#{completedCredits} earned', {
        completedCredits,
      })
    : '';
  if (completedCreditsText) {
    creditsCompletionTextList.push(completedCreditsText);
  }

  // the text is hidden if 0 credits are in progress
  const inProgressCreditsText = inProgressCredits
    ? _t('#{inProgressCredits} in progress', {
        inProgressCredits,
      })
    : '';
  if (inProgressCreditsText) {
    creditsCompletionTextList.push(inProgressCreditsText);
  }

  return creditsCompletionTextList;
};

/**
 * Calculate the progression (credits summary and warnings) for the curriculums,
 * as well as the categories and segments under each curriculum
 * @param curriculums the list of curriculums
 * @returns the progression result
 */
export const getProgression = (
  curriculums: (DpDegreeCurriculum | DpPbaCurriculum)[],
  gradeScale: GradeScaleFragment
): ProgressionCalculatorResult => {
  const curriculumsProgression: ProgressionCalculatorResult['curriculums'] = {};
  const categoriesProgression: ProgressionCalculatorResult['categories'] = {};
  const segmentsProgression: ProgressionCalculatorResult['segments'] = {};

  curriculums.forEach((curriculum) => {
    const categories = curriculum.categories;
    const categoriesCompletionRuleResults: boolean[] = [];
    const categoriesHaveWarningsResults: boolean[] = [];

    const completedCoursesDictionary: Record<string, DpPersonalizedSegmentCourse> = {};
    const inProgressCoursesDictionary: Record<string, DpPersonalizedSegmentCourse> = {};
    const plannedCoursesDictionary: Record<string, DpPersonalizedSegmentCourse> = {};

    categories.forEach((category) => {
      // use the category-level min passing grade if provided, otherwise use the curriculum-level one as the fallback
      const minPassingGradeForCategory = category.minPassingGradeForCourses ?? curriculum.minPassingGradeForCourses;
      const segments = category.segments;
      segments.forEach((segment) => {
        const { completedCourses, inProgressCourses, plannedCourses, ...segmentCreditsSummary } =
          getCreditsSummaryForSingleSegment(minPassingGradeForCategory, segment);

        // Collect for curriculum-level progress calculation
        completedCourses.forEach((course) => {
          completedCoursesDictionary[course.segmentCourse.id] = course;
        });
        inProgressCourses.forEach((course) => {
          inProgressCoursesDictionary[course.segmentCourse.id] = course;
        });

        plannedCourses.forEach((course) => {
          plannedCoursesDictionary[course.segmentCourse.id] = course;
        });

        segmentsProgression[segment.id] = {
          summary: segmentCreditsSummary,
          completionRuleMet: segmentCreditsSummary.completed >= segment.completionRule.minUnits,
        };
      });
      // check the number of complete segments under the category
      const completeSegmentsUnderCategory = segments.filter(
        (segment) => segmentsProgression[segment.id]?.completionRuleMet
      );
      // the category's completion rule is met if the number of complete segments under the category
      // is equal or larger than the min units required by the completion rule
      const isCategoryCompletionRuleMet = completeSegmentsUnderCategory.length >= category.completionRule.minUnits;
      const { categoryGPA, ...categoryWarnings } = getWarningsForSingleCategory(
        category,
        isCategoryCompletionRuleMet,
        gradeScale
      );
      categoriesProgression[category.id] = {
        warnings: categoryWarnings,
        categoryGPA,
        completionRuleMet: isCategoryCompletionRuleMet,
      };

      categoriesCompletionRuleResults.push(isCategoryCompletionRuleMet);
      categoriesHaveWarningsResults.push(Object.values(categoryWarnings).some((hasWarning) => hasWarning));
    });

    const curriculumCreditsSummary = {
      completed: getSumOfCredits(Object.values(completedCoursesDictionary)),
      inProgress: getSumOfCredits(Object.values(inProgressCoursesDictionary)),
      planned: getSumOfCredits(Object.values(plannedCoursesDictionary)),
    };

    // only calculate the curriculum-level warnings if all of the followings are true:
    // 1. the learner already completed more credits than required by the curriculum (enforced by getWarningsForSingleCurriculum)
    // 2. all underlying categories' completion rules are met (enforced by getWarningsForSingleCurriculum)
    // 3. no underlying categories have category-level warnings (enforced by getWarningsForSingleCurriculum)
    const shouldCalculateCurriculumWarnings =
      curriculumCreditsSummary.completed >= curriculum.minCredits &&
      categoriesCompletionRuleResults.every((completionRuleResult) => completionRuleResult) &&
      categoriesHaveWarningsResults.every((hasWarning) => !hasWarning);

    let curriculumGPAResult = null;
    let curriculumWarnings = { gpa: false, grade: false };

    if (shouldCalculateCurriculumWarnings) {
      const hasGradeWarning = curriculumHasGradeWarning(curriculum);
      const { hasGPAWarning, curriculumGPA } = curriculumHasGPAWarning(curriculum, gradeScale);
      curriculumWarnings = { gpa: hasGPAWarning, grade: hasGradeWarning };
      curriculumGPAResult = curriculumGPA;
    }

    curriculumsProgression[curriculum.id] = {
      summary: curriculumCreditsSummary,
      warnings: curriculumWarnings,
      curriculumGPA: curriculumGPAResult,
      creditsAndCategoryRequirementsMet: shouldCalculateCurriculumWarnings,
    };
  });

  return {
    curriculums: curriculumsProgression,
    categories: categoriesProgression,
    segments: segmentsProgression,
  };
};
